import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DashboardApi } from '../services/api';
import { AuthApi } from '../services/auth';
import {
  Session,
  clearSession,
  getToken,
  getUser,
  getActiveUser,
  getSelectedCompanyId,
  notifyLogout,
  storeSession,
  storeActiveUser,
  storeSelectedCompanyId,
} from '../services/session';

const normalizeActiveUser = (value) => {
  if (!value) return null;
  const rawUserId = value.userId ?? value.user_id ?? value.accountId ?? value.account_id;
  if (!rawUserId) return null;
  const userId = String(rawUserId);
  const companyId = value.companyId ?? value.company_id ?? null;
  const displayName =
    value.displayName ||
    value.companyName ||
    value.login ||
    value.email ||
    'Kunde';
  return {
    userId,
    displayName,
    companyName: value.companyName || null,
    companyId: companyId ? String(companyId) : null,
    email: value.email || null,
    logoUrl: value.logoUrl || null,
    planTier: value.planTier || null,
    impersonated: Boolean(value.impersonated),
  };
};

const mapCompanyToActiveUser = (company, accountId) => {
  if (!company || company.id === undefined || company.id === null) {
    return null;
  }
  const companyId = String(company.id);
  const resolvedUserId = companyId || (accountId ? String(accountId) : null);
  return {
    userId: resolvedUserId,
    displayName: company.name || 'Selskap',
    companyName: company.name || null,
    companyId,
    email: company.billingEmail || company.primaryEmail || null,
    logoUrl: company.logoUrl || null,
    planTier: company.planTier || null,
    impersonated: false,
  };
};

const normalizeRole = (company, user) => {
  if (!company) return 'custom';
  // Super-admins get owner-level access
  if (user?.isAdmin || company.isSuperAdminAccess) return 'owner';
  if (company.isOwner) return 'owner';
  return 'custom';
};

const createNoAccessPermissions = () => ({
  role: 'custom',
  permissionKeys: [],
  productScopeMode: 'selected',
  allowedProductIds: [],
  allowUnmatchedInbox: false,
  isViewer: false,
  isAgent: false,
  isManager: false,
  isOwner: false,
  canViewDashboard: true,
  canViewAgent: false,
  canUseAgent: false,
  canViewInnsikt: false,
  canViewHenvendelser: false,
  canViewInquiryOverview: false,
  canViewInbox: false,
  canViewInquiryTypes: false,
  canViewForms: false,
  canViewProducts: false,
  canViewChatHistory: false,
  canViewBots: false,
  canViewCompany: false,
  canViewCompanyOverview: false,
  canViewMembers: false,
  canViewAccess: false,
  canViewCompanySettings: false,
  canViewAddons: false,
  canViewBilling: false,
  canViewBooking: false,
  canEditBots: false,
  canManageBots: false,
  canEditKnowledge: false,
  canViewKnowledge: false,
  canManageKnowledge: false,
  canManageInbox: false,
  canManageInquiryTypes: false,
  canManageForms: false,
  canManageProducts: false,
  canManageMembers: false,
  canManageAccess: false,
  canManageRoles: false,
  canManageCompany: false,
  canManageSettings: false,
  canManageAddons: false,
  canManageBilling: false,
  canInviteMembers: false,
});

const normalizePermissions = (company, user) => {
  const base = createNoAccessPermissions();
  const serverPermissions = company?.permissions && typeof company.permissions === 'object'
    ? company.permissions
    : {};
  const merged = {
    ...base,
    ...serverPermissions,
  };
  if (user?.isAdmin || company?.isSuperAdminAccess || company?.isOwner) {
    return {
      ...merged,
      role: 'owner',
      isOwner: true,
      isAgent: true,
      isManager: true,
      canViewDashboard: true,
      canViewAgent: true,
      canUseAgent: true,
      canViewInnsikt: true,
      canViewHenvendelser: true,
      canViewInquiryOverview: true,
      canViewInbox: true,
      canViewInquiryTypes: true,
      canViewForms: true,
      canViewProducts: true,
      canViewChatHistory: true,
      canViewBots: true,
      canViewCompany: true,
      canViewCompanyOverview: true,
      canViewMembers: true,
      canViewAccess: true,
      canViewCompanySettings: true,
      canViewAddons: true,
      canViewBilling: true,
      canViewBooking: true,
      canEditBots: true,
      canManageBots: true,
      canEditKnowledge: true,
      canViewKnowledge: true,
      canManageKnowledge: true,
      canManageInbox: true,
      canManageInquiryTypes: true,
      canManageForms: true,
      canManageProducts: true,
      canManageMembers: true,
      canManageAccess: true,
      canManageRoles: true,
      canManageCompany: true,
      canManageSettings: true,
      canManageAddons: true,
      canManageBilling: true,
      canInviteMembers: true,
    };
  }
  return {
    ...merged,
    role: 'custom',
    canViewDashboard: true,
  };
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const initialUser = getUser();
  const [token, setToken] = useState(() => getToken());
  const [user, setUser] = useState(initialUser);
  const [activeUser, setActiveUser] = useState(() => normalizeActiveUser(getActiveUser()) || normalizeActiveUser(initialUser));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountProfile, setAccountProfile] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);
  const [companiesInitialized, setCompaniesInitialized] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(() => getSelectedCompanyId());
  const [requiresRegistration, setRequiresRegistration] = useState(false);
  const selectedCompanyRef = useRef(selectedCompanyId);
  const selectedCompany = useMemo(
    () => companies.find((company) => String(company.id) === String(selectedCompanyId)) || null,
    [companies, selectedCompanyId],
  );

  const clearError = useCallback(() => setError(null), []);

  const persistSelectedCompany = useCallback((companyId) => {
    if (companyId === null || companyId === undefined || companyId === '') {
      setSelectedCompanyId(null);
      storeSelectedCompanyId(null);
      return;
    }
    setSelectedCompanyId(String(companyId));
    storeSelectedCompanyId(String(companyId));
  }, []);

  useEffect(() => {
    selectedCompanyRef.current = selectedCompanyId;
  }, [selectedCompanyId]);

  const refreshCompanies = useCallback(async () => {
    if (!token) {
      setAccountProfile(null);
      setCompanies([]);
      setCompaniesError(null);
      setCompaniesInitialized(false);
      setRequiresRegistration(false);
      persistSelectedCompany(null);
      return;
    }
    setCompaniesLoading(true);
    try {
      const payload = await DashboardApi.getAccountContext();
      const {
        account,
        companies: list = [],
        selectedCompanyId: serverSelectedId,
      } = payload || {};
      const normalizedList = Array.isArray(list) ? list : [];
      setAccountProfile(account || null);
      setCompanies(normalizedList);
      setCompaniesError(null);
      setCompaniesInitialized(true);
      setRequiresRegistration(false);

      if (normalizedList.length === 0) {
        persistSelectedCompany(null);
        return;
      }

      const preferredId = serverSelectedId ?? selectedCompanyRef.current;
      if (
        preferredId
        && normalizedList.some((company) => String(company.id) === String(preferredId))
      ) {
        persistSelectedCompany(preferredId);
        return;
      }

      persistSelectedCompany(null);
    } catch (err) {
      // Check if this is an "account not registered" error
      if (err.code === 'ACCOUNT_NOT_REGISTERED' || err.details?.requiresRegistration) {
        setRequiresRegistration(true);
        setCompaniesError(null);
        setCompaniesInitialized(true);
        return; // Don't throw - this is a handled state
      }
      setCompaniesError(err.message || 'Kunne ikke hente selskaper.');
      setCompaniesInitialized(true);
      throw err;
    } finally {
      setCompaniesLoading(false);
    }
  }, [persistSelectedCompany, token]);

  useEffect(() => {
    if (!token) {
      setAccountProfile(null);
      setCompanies([]);
      setCompaniesError(null);
      setCompaniesInitialized(false);
      persistSelectedCompany(null);
      return;
    }
    refreshCompanies().catch(() => {});
  }, [token, refreshCompanies, persistSelectedCompany]);

  const login = useCallback(
    async (identifier, password) => {
      setLoading(true);
      setError(null);
      setRequiresRegistration(false);
      try {
        const result = await AuthApi.login(identifier, password);
        storeSession(result.token, result.user);
        setToken(result.token);
        setUser(result.user);
        setAccountProfile(null);
        setCompanies([]);
        setCompaniesError(null);
        setCompaniesInitialized(false);
        setRequiresRegistration(false);
        persistSelectedCompany(null);
        const baseActiveUser = normalizeActiveUser({ ...result.user, impersonated: false });
        setActiveUser(baseActiveUser);
        storeActiveUser(baseActiveUser);
        return result;
      } catch (err) {
        const message = err.message || 'Kunne ikke logge inn';
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [persistSelectedCompany],
  );

  const applySession = useCallback(
    (result) => {
      if (!result?.token || !result?.user) {
        throw new Error('Ugyldig sesjonsdata');
      }
      storeSession(result.token, result.user);
      setToken(result.token);
      setUser(result.user);
      setAccountProfile(null);
      setCompanies([]);
      setCompaniesError(null);
      setCompaniesInitialized(false);
      setRequiresRegistration(false);
      persistSelectedCompany(null);
      const baseActiveUser = normalizeActiveUser({ ...result.user, impersonated: false });
      setActiveUser(baseActiveUser);
      storeActiveUser(baseActiveUser);
      setError(null);
      return result;
    },
    [persistSelectedCompany],
  );

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
    setUser(null);
    setActiveUser(null);
    setAccountProfile(null);
    setCompanies([]);
    setCompaniesError(null);
    setCompaniesInitialized(false);
    setRequiresRegistration(false);
    persistSelectedCompany(null);
    storeActiveUser(null);
    notifyLogout();
  }, [persistSelectedCompany]);

  const selectCompany = useCallback(
    async (companyId) => {
      if (!companyId) {
        persistSelectedCompany(null);
        return;
      }
      persistSelectedCompany(companyId);
      try {
        await DashboardApi.selectCompany(companyId);
      } catch (err) {
        setCompaniesError(err.message || 'Kunne ikke bytte selskap.');
        throw err;
      }
    },
    [persistSelectedCompany],
  );

  const createCompany = useCallback(
    async (payload = {}) => {
      try {
        const response = await DashboardApi.createCompany(payload);
        await refreshCompanies();
        const createdCompanyId = response?.company?.id ?? response?.id ?? null;
        if (createdCompanyId) {
          await selectCompany(createdCompanyId);
        }
        return response;
      } catch (err) {
        setCompaniesError(err.message || 'Kunne ikke opprette selskap.');
        throw err;
      }
    },
    [refreshCompanies, selectCompany],
  );

  const impersonateUser = useCallback(
    (target) => {
      if (!user?.isAdmin || !target) return;
      const payload = normalizeActiveUser({
        ...target,
        impersonated: true,
      });
      setActiveUser(payload);
      storeActiveUser(payload);
    },
    [user],
  );

  const clearImpersonation = useCallback(() => {
    if (!user) return;
    const baseActiveUser = normalizeActiveUser({ ...user, impersonated: false });
    setActiveUser(baseActiveUser);
    storeActiveUser(baseActiveUser);
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleLogout = () => {
      setToken(null);
      setUser(null);
      setActiveUser(null);
      setAccountProfile(null);
      setCompanies([]);
      setCompaniesInitialized(false);
      persistSelectedCompany(null);
    };
    window.addEventListener(Session.LOGOUT_EVENT, handleLogout);
    return () => window.removeEventListener(Session.LOGOUT_EVENT, handleLogout);
  }, [persistSelectedCompany]);

  useEffect(() => {
    if (!user) {
      setActiveUser(null);
      storeActiveUser(null);
      return;
    }

    if (user.isAdmin) {
      setActiveUser((current) => {
        if (!current || String(current.userId) === String(user.userId)) {
          const base = normalizeActiveUser({ ...user, impersonated: false });
          storeActiveUser(base);
          return base;
        }
        return current;
      });
      return;
    }

    const mapped = mapCompanyToActiveUser(selectedCompany, user?.userId);
    setActiveUser(mapped);
    storeActiveUser(mapped);
  }, [selectedCompany, user]);

  // Computed role and permission helpers
  const userRole = useMemo(
    () => normalizeRole(selectedCompany, user),
    [selectedCompany, user],
  );

  const permissions = useMemo(() => {
    return normalizePermissions(selectedCompany, user);
  }, [selectedCompany, user]);

  const value = useMemo(
    () => ({
      token,
      user,
      accountProfile,
      companies,
      companiesLoading,
      companiesError,
      companiesInitialized,
      selectedCompany,
      selectedCompanyId,
      activeUser,
      activeUserId: activeUser?.userId ?? null,
      requiresRegistration,
      login,
      logout,
      impersonateUser,
      clearImpersonation,
      selectCompany,
      createCompany,
      refreshCompanies,
      applySession,
      loading,
      error,
      clearError,
      isAuthenticated: Boolean(token && user),
      isImpersonating: Boolean(user?.isAdmin && activeUser && String(activeUser.userId) !== String(user.userId)),
      // Role-based permissions
      userRole,
      permissions,
    }),
    [
      token,
      user,
      accountProfile,
      companies,
      companiesLoading,
      companiesError,
      companiesInitialized,
      selectedCompany,
      selectedCompanyId,
      activeUser,
      requiresRegistration,
      login,
      logout,
      impersonateUser,
      clearImpersonation,
      selectCompany,
      createCompany,
      refreshCompanies,
      applySession,
      loading,
      error,
      clearError,
      userRole,
      permissions,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
